import { useEffect, useState } from 'react'
import {
  Bell,
  Database,
  Globe,
  Palette,
  Shield,
  User,
} from 'lucide-react'
import { TopBar } from '../components/TopBar'
import { useI18n } from '../lib/i18n'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useTheme } from '../lib/theme'

// ─── i18n ────────────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '设置',
    tabs: {
      profile: '个人资料',
      notifications: '通知设置',
      security: '安全与隐私',
      data: '数据管理',
      appearance: '外观设置',
      language: '语言设置',
    },
    // 个人资料
    profileTitle: '个人资料',
    changeAvatar: '更换头像',
    avatarHint: '支持 JPG、PNG 格式，不超过 2MB',
    nameLabel: '姓名',
    emailLabel: '邮箱',
    phoneLabel: '电话',
    firmLabel: '所属律所',
    saveChanges: '保存更改',
    // 通知设置
    notifTitle: '通知设置',
    emailNotif: '邮件通知',
    emailNotifDesc: '接收案件更新和系统通知到邮箱',
    pushNotif: '推送通知',
    pushNotifDesc: '在浏览器中显示实时通知',
    smsNotif: '短信通知',
    smsNotifDesc: '接收重要事项的短信提醒',
    // 安全与隐私
    securityTitle: '安全与隐私',
    changePassword: '修改密码',
    changePasswordDesc: '更新您的账号密码',
    changePasswordBtn: '修改密码',
    twoFactor: '双因素认证',
    twoFactorDesc: '启用额外的安全验证',
    twoFactorBtn: '启用认证',
    // 数据管理
    dataTitle: '数据管理',
    exportData: '导出数据',
    exportDataDesc: '导出所有案件和知识库数据',
    exportBtn: '导出全部数据',
    backup: '备份设置',
    backupDesc: '配置自动备份频率和存储位置',
    backupBtn: '配置备份',
    // 外观设置
    appearanceTitle: '外观设置',
    themeLabel: '主题',
    themeLight: '浅色',
    themeDark: '深色',
    themeSystem: '跟随系统',
    // 语言设置
    langTitle: '语言设置',
    uiLangLabel: '界面语言',
    docLangLabel: '文书输出语言',
    docLangHint: '文书生成将按照所选语言生成符合当地法律版规范的格式',
    langZh: '简体中文',
    langEn: 'English',
    docLangEn: '英文（马来西亚法庭标准）',
    docLangZh: '中文',
  },
  en: {
    pageTitle: 'Settings',
    tabs: {
      profile: 'Profile',
      notifications: 'Notifications',
      security: 'Security & Privacy',
      data: 'Data Management',
      appearance: 'Appearance',
      language: 'Language',
    },
    profileTitle: 'Profile',
    changeAvatar: 'Change Avatar',
    avatarHint: 'JPG, PNG format, max 2MB',
    nameLabel: 'Name',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    firmLabel: 'Law Firm',
    saveChanges: 'Save Changes',
    notifTitle: 'Notifications',
    emailNotif: 'Email Notifications',
    emailNotifDesc: 'Receive case updates and system notifications via email',
    pushNotif: 'Push Notifications',
    pushNotifDesc: 'Show real-time notifications in browser',
    smsNotif: 'SMS Notifications',
    smsNotifDesc: 'Receive SMS for important events',
    securityTitle: 'Security & Privacy',
    changePassword: 'Change Password',
    changePasswordDesc: 'Update your account password',
    changePasswordBtn: 'Change Password',
    twoFactor: 'Two-Factor Authentication',
    twoFactorDesc: 'Enable additional security verification',
    twoFactorBtn: 'Enable 2FA',
    dataTitle: 'Data Management',
    exportData: 'Export Data',
    exportDataDesc: 'Export all cases and knowledge base data',
    exportBtn: 'Export All Data',
    backup: 'Backup Settings',
    backupDesc: 'Configure automatic backup frequency and storage',
    backupBtn: 'Configure Backup',
    appearanceTitle: 'Appearance',
    themeLabel: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    langTitle: 'Language',
    uiLangLabel: 'Interface Language',
    docLangLabel: 'Document Output Language',
    docLangHint: 'Documents will be generated in the selected language following local legal standards',
    langZh: '简体中文',
    langEn: 'English',
    docLangEn: 'English (Malaysian Court Standard)',
    docLangZh: 'Chinese',
  },
}

type SettingsTab = 'profile' | 'notifications' | 'security' | 'data' | 'appearance' | 'language'

const TAB_ICONS: Record<SettingsTab, React.ComponentType<{ className?: string }>> = {
  profile: User,
  notifications: Bell,
  security: Shield,
  data: Database,
  appearance: Palette,
  language: Globe,
}

const TAB_ORDER: SettingsTab[] = ['profile', 'notifications', 'security', 'data', 'appearance', 'language']

export function Settings() {
  const { lang, setLang, docLang, setDocLang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh
  const { user } = useCurrentUser()

  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')

  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex gap-6 max-w-4xl">
          {/* 左侧 tab 导航 */}
          <div className="w-52 flex-shrink-0">
            <h3 className="text-sm font-semibold text-brand-800 mb-3 px-3">
              {t.pageTitle}
            </h3>
            <nav className="space-y-0.5">
              {TAB_ORDER.map((tab) => {
                const Icon = TAB_ICONS[tab]
                const label = t.tabs[tab]
                const isActive = activeTab === tab
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-brand-600 hover:bg-brand-50 hover:text-brand-800'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                )
              })}
            </nav>
          </div>

          {/* 右侧内容 */}
          <div className="flex-1 min-w-0">
            {activeTab === 'profile' && <ProfilePanel t={t} user={user} />}
            {activeTab === 'notifications' && <NotificationsPanel t={t} />}
            {activeTab === 'security' && <SecurityPanel t={t} />}
            {activeTab === 'data' && <DataPanel t={t} />}
            {activeTab === 'appearance' && <AppearancePanel t={t} />}
            {activeTab === 'language' && <LanguagePanel t={t} lang={lang} setLang={setLang} docLang={docLang} setDocLang={setDocLang} />}
          </div>
        </div>
      </div>
    </>
  )
}

// ─── 个人资料 ────────────────────────────────────────────────────────────────
function ProfilePanel({ t, user, onUserUpdated }: { t: (typeof LABELS)['zh']; user: any; onUserUpdated?: (u: any) => void }) {
  const initial = user?.display_name?.charAt(0) ?? '?'

  const [name, setName] = useState(user?.display_name ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [phone, setPhone] = useState(user?.phone ?? '')
  const [firm, setFirm] = useState(user?.firm_name ?? '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleSave = () => {
    setSaving(true)
    setSaved(false)
    fetch('/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        display_name: name || undefined,
        email: email || undefined,
        phone: phone || undefined,
        firm_name: firm || undefined,
      }),
    })
      .then((r) => {
        if (r.ok) {
          setSaved(true)
          r.json().then((u) => { if (onUserUpdated) onUserUpdated(u) })
          setTimeout(() => setSaved(false), 2000)
        }
      })
      .catch(() => {})
      .finally(() => setSaving(false))
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-brand-800 mb-5">{t.profileTitle}</h3>

      {/* 头像 */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
          {initial}
        </div>
        <div>
          <button
            type="button"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {t.changeAvatar}
          </button>
          <p className="text-xs text-brand-400 mt-0.5">{t.avatarHint}</p>
        </div>
      </div>

      {/* 表单 */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <FormField label={t.nameLabel} value={name} onChange={setName} />
        <FormField label={t.emailLabel} value={email} onChange={setEmail} />
        <FormField label={t.phoneLabel} value={phone} onChange={setPhone} />
        <FormField label={t.firmLabel} value={firm} onChange={setFirm} />
      </div>

      <div className="flex items-center justify-end gap-3">
        {saved && (
          <span className="text-sm text-green-600 font-medium">
            ✓ {t.saveChanges === '保存更改' ? '已保存' : 'Saved'}
          </span>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          💾 {t.saveChanges}
        </button>
      </div>
    </div>
  )
}

function FormField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-sm text-brand-600 mb-1.5">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-brand-200 text-sm text-brand-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
      />
    </div>
  )
}

// ─── 通知设置 ────────────────────────────────────────────────────────────────
function NotificationsPanel({ t }: { t: (typeof LABELS)['zh'] }) {
  const [emailOn, setEmailOn] = useState(true)
  const [pushOn, setPushOn] = useState(true)
  const [smsOn, setSmsOn] = useState(false)

  // Load preferences from backend
  useEffect(() => {
    fetch('/me/notification-preferences')
      .then((r) => r.json())
      .then((data) => {
        if (data.email_on !== undefined) setEmailOn(data.email_on)
        if (data.push_on !== undefined) setPushOn(data.push_on)
        if (data.sms_on !== undefined) setSmsOn(data.sms_on)
      })
      .catch(() => {})
  }, [])

  // Save preferences when toggled
  const savePrefs = (email: boolean, push: boolean, sms: boolean) => {
    fetch('/me/notification-preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email_on: email, push_on: push, sms_on: sms }),
    }).catch(() => {})
  }

  const toggleEmail = () => { const v = !emailOn; setEmailOn(v); savePrefs(v, pushOn, smsOn) }
  const togglePush = () => { const v = !pushOn; setPushOn(v); savePrefs(emailOn, v, smsOn) }
  const toggleSms = () => { const v = !smsOn; setSmsOn(v); savePrefs(emailOn, pushOn, v) }

  return (
    <div>
      <h3 className="text-lg font-semibold text-brand-800 mb-5">{t.notifTitle}</h3>
      <div className="space-y-5">
        <ToggleRow title={t.emailNotif} desc={t.emailNotifDesc} on={emailOn} onToggle={toggleEmail} />
        <ToggleRow title={t.pushNotif} desc={t.pushNotifDesc} on={pushOn} onToggle={togglePush} />
        <ToggleRow title={t.smsNotif} desc={t.smsNotifDesc} on={smsOn} onToggle={toggleSms} />
      </div>
    </div>
  )
}

function ToggleRow({
  title,
  desc,
  on,
  onToggle,
}: {
  title: string
  desc: string
  on: boolean
  onToggle: () => void
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-brand-100 last:border-0">
      <div>
        <div className="text-sm font-medium text-brand-800">{title}</div>
        <div className="text-xs text-brand-400 mt-0.5">{desc}</div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className={`relative w-11 h-6 rounded-full transition-colors ${
          on ? 'bg-blue-600' : 'bg-brand-300'
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            on ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  )
}

// ─── 安全与隐私 ──────────────────────────────────────────────────────────────
function SecurityPanel({ t }: { t: (typeof LABELS)['zh'] }) {
  const [showPwModal, setShowPwModal] = useState(false)
  const [oldPw, setOldPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [pwLoading, setPwLoading] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwSuccess, setPwSuccess] = useState('')

  const handleChangePassword = async () => {
    setPwError('')
    setPwSuccess('')
    if (newPw.length < 6) { setPwError('密码至少6位'); return }
    if (newPw !== confirmPw) { setPwError('两次密码不一致'); return }
    setPwLoading(true)
    try {
      const res = await fetch('/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_password: oldPw, new_password: newPw }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        setPwError(data?.detail || '修改失败')
        return
      }
      setPwSuccess('密码修改成功！')
      setOldPw(''); setNewPw(''); setConfirmPw('')
      setTimeout(() => setShowPwModal(false), 1500)
    } catch { setPwError('网络错误') }
    finally { setPwLoading(false) }
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-brand-800 mb-5">{t.securityTitle}</h3>
      <div className="space-y-4">
        <div className="rounded-xl border border-brand-200 p-5">
          <h4 className="text-sm font-semibold text-brand-800">{t.changePassword}</h4>
          <p className="text-xs text-brand-400 mt-1 mb-3">{t.changePasswordDesc}</p>
          <button
            type="button"
            onClick={() => { setShowPwModal(true); setPwError(''); setPwSuccess('') }}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-brand-300 text-brand-700 hover:bg-brand-50"
          >
            {t.changePasswordBtn}
          </button>
        </div>
        <ActionCard title={t.twoFactor} desc={t.twoFactorDesc} btnLabel={t.twoFactorBtn} variant="outline" />
      </div>

      {/* 修改密码弹窗 */}
      {showPwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">{t.changePassword}</h4>
            {pwError && <div className="mb-3 p-2 rounded-lg bg-red-50 text-sm text-red-600">{pwError}</div>}
            {pwSuccess && <div className="mb-3 p-2 rounded-lg bg-green-50 text-sm text-green-600">{pwSuccess}</div>}
            <div className="space-y-3">
              <input
                type="password"
                placeholder="当前密码"
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
              />
              <input
                type="password"
                placeholder="新密码（至少6位）"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
              />
              <input
                type="password"
                placeholder="确认新密码"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => setShowPwModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={pwLoading}
                className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
              >
                {pwLoading ? '提交中...' : '确认修改'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── 数据管理 ────────────────────────────────────────────────────────────────
function DataPanel({ t }: { t: (typeof LABELS)['zh'] }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-brand-800 mb-5">{t.dataTitle}</h3>
      <div className="space-y-4">
        <ActionCard title={t.exportData} desc={t.exportDataDesc} btnLabel={t.exportBtn} variant="primary" />
        <ActionCard title={t.backup} desc={t.backupDesc} btnLabel={t.backupBtn} variant="outline" />
      </div>
    </div>
  )
}

function ActionCard({
  title,
  desc,
  btnLabel,
  variant = 'outline',
}: {
  title: string
  desc: string
  btnLabel: string
  variant?: 'primary' | 'outline'
}) {
  const btnClass =
    variant === 'primary'
      ? 'bg-blue-600 text-white hover:bg-blue-700'
      : 'border border-brand-300 text-brand-700 hover:bg-brand-50'

  return (
    <div className="rounded-xl border border-brand-200 p-5">
      <h4 className="text-sm font-semibold text-brand-800">{title}</h4>
      <p className="text-xs text-brand-400 mt-1 mb-3">{desc}</p>
      <button
        type="button"
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${btnClass}`}
      >
        {btnLabel}
      </button>
    </div>
  )
}

// ─── 外观设置 ────────────────────────────────────────────────────────────────
function AppearancePanel({ t }: { t: (typeof LABELS)['zh'] }) {
  const { theme, setTheme } = useTheme()

  const themes: { key: 'light' | 'dark' | 'system'; label: string; preview: string }[] = [
    { key: 'light', label: t.themeLight, preview: 'bg-white border-brand-200' },
    { key: 'dark', label: t.themeDark, preview: 'bg-brand-800 border-brand-700' },
    { key: 'system', label: t.themeSystem, preview: 'bg-gradient-to-r from-white to-brand-800 border-brand-300' },
  ]

  return (
    <div>
      <h3 className="text-lg font-semibold text-brand-800 mb-2">{t.appearanceTitle}</h3>
      <p className="text-sm text-brand-500 mb-4">{t.themeLabel}</p>
      <div className="flex gap-4">
        {themes.map((th) => (
          <button
            key={th.key}
            type="button"
            onClick={() => setTheme(th.key)}
            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors w-36 ${
              theme === th.key
                ? 'border-blue-500 bg-blue-50'
                : 'border-brand-200 hover:border-brand-300'
            }`}
          >
            <div className={`w-12 h-8 rounded-lg border ${th.preview}`} />
            <span className="text-sm text-brand-700 font-medium">{th.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── 语言设置 ────────────────────────────────────────────────────────────────
function LanguagePanel({
  t,
  lang,
  setLang,
  docLang,
  setDocLang,
}: {
  t: (typeof LABELS)['zh']
  lang: string
  setLang: (l: 'zh' | 'en') => void
  docLang: string
  setDocLang: (l: 'zh' | 'en') => void
}) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-brand-800 mb-5">{t.langTitle}</h3>

      {/* 界面语言 */}
      <div className="mb-5">
        <label className="block text-sm text-brand-600 mb-1.5">{t.uiLangLabel}</label>
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value as 'zh' | 'en')}
          className="w-full max-w-md px-3 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 bg-white"
        >
          <option value="zh">{t.langZh}</option>
          <option value="en">{t.langEn}</option>
        </select>
      </div>

      {/* 文书输出语言 */}
      <div>
        <label className="block text-sm text-brand-600 mb-1.5">{t.docLangLabel}</label>
        <select
          value={docLang}
          onChange={(e) => setDocLang(e.target.value as 'zh' | 'en')}
          className="w-full max-w-md px-3 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 bg-white"
        >
          <option value="en">{t.docLangEn}</option>
          <option value="zh">{t.docLangZh}</option>
        </select>
        <p className="text-xs text-brand-400 mt-1.5">{t.docLangHint}</p>
      </div>
    </div>
  )
}
